import { useState } from 'react'

import type { ReactElement } from 'react'
import type { IAppSummary } from '~/types/application'
import type { MobileDeviceType } from '~/types/device-type'
import type { ITemplateParams } from '~/types/template'

import TemplateSelector from './TemplateSelector'
import TemplateVersionSelector from './TemplateVersionSelector'

import { useTemplateListQuery } from '../../../../Templates/queries'

import Modal from '~/components/Modal'
import { ModalHeader } from '~/components/Modal/Layout'
import { DEFAULT_ITEMS_PER_PAGE_VALUE } from '~/const'
import AppSelector from '~/views/DynamicScreens/AppSelector'

import './styles.scss'

interface Props {
  apps?: IAppSummary[] | null
  isOpen: boolean
  onAppRowClick: (app: { app_key: string; device_type: MobileDeviceType }) => void
  onClose: VoidFunction
  onTemplateClick: (params: ITemplateParams) => void
  selectedApp?: IAppSummary
}

const modalTitles = {
  app: '1 / 3 - Choose your app',
  template: '2 / 3 - Choose your template',
  templateVersion: '3 / 3 - Choose your template version',
}

const AppTemplateSelectorModal = ({
  isOpen,
  onAppRowClick,
  onClose,
  onTemplateClick,
  selectedApp,
}: Props): ReactElement => {
  const [selection, setSelection] = useState<'app' | 'template' | 'templateVersion'>('app')

  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string>()

  const { data: templates, isLoading: isTemplatesLoading } = useTemplateListQuery(
    {
      'device-type': selectedApp?.device_type,
      size: DEFAULT_ITEMS_PER_PAGE_VALUE,
    },
    { enabled: !!selectedApp }
  )

  const handleAppSelectorRowClicked = ({
    app_key,
    device_type,
  }: {
    app_key: string
    device_type: MobileDeviceType
  }) => {
    onAppRowClick({ app_key, device_type })
    setSelection('template')
  }

  const handleTemplateClick = (key: string) => {
    setSelectedTemplateKey(key)
    setSelection('templateVersion')
  }

  const handleTemplateVersionClick = (params: ITemplateParams) => {
    onTemplateClick(params)
    setSelection('app')
  }

  const handleBackClick = (state: 'app' | 'template') => () => {
    setSelection(state)
  }

  return (
    <Modal className="app-selector-modal with-template" isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>{modalTitles[selection]}</ModalHeader>
      {selection === 'app' && (
        <AppSelector
          onAppSelectorRowClicked={handleAppSelectorRowClicked}
          selectedApp={{
            id: selectedApp?.app_id,
            name: selectedApp?.name,
          }}
        />
      )}
      {selection === 'template' && (
        <TemplateSelector
          isLoading={isTemplatesLoading}
          onBackClick={handleBackClick('app')}
          onTemplateClick={handleTemplateClick}
          selectedApp={selectedApp}
          templates={templates?.data || []}
        />
      )}
      {selection === 'templateVersion' && (
        <TemplateVersionSelector
          isLoading={isTemplatesLoading}
          onBackClick={handleBackClick('template')}
          onTemplateClick={handleTemplateVersionClick}
          selectedTemplateKey={selectedTemplateKey}
          templates={templates?.data || []}
        />
      )}
    </Modal>
  )
}

export default AppTemplateSelectorModal
